import React, { useState, useEffect, useMemo, useRef } from 'react';
import { XMarkIcon, VideoCameraIcon, CurrencyDollarIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { BatchGenerateVideosRequest, ModelType, IVideoClip, IUserModel, Resolution } from '@packages/global';
import { useModelStore } from '../../stores/modelStore';
import { useTranslation } from 'react-i18next';

// 紧凑型模型选择器
function CompactModelSelector({
  label,
  models,
  value,
  onChange,
  disabled = false,
  defaultModelLabel
}: {
  label: string;
  models: IUserModel[];
  value: string;
  onChange: (modelId: string) => void;
  disabled?: boolean;
  defaultModelLabel: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedModel = models.find(m => m.id === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2 bg-bg-form text-sm rounded-lg border transition-colors ${isOpen ? 'border-primary' : 'border-border-subtle hover:border-border-default'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className={`truncate ${selectedModel ? 'text-gray-50' : 'text-gray-500'}`}>
          {selectedModel?.title || defaultModelLabel}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && models.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-bg-form border border-border-subtle rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
          {models.map(model => (
            <button
              key={model.id}
              type="button"
              onClick={() => {
                onChange(model.id);
                setIsOpen(false);
              }}
              className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors cursor-pointer ${value === model.id ? 'text-primary bg-bg-elevated/50' : 'text-gray-200'
                }`}
            >
              <span className="truncate">{model.title}</span>
              {value === model.id && (
                <CheckIcon className="w-4 h-4 text-primary flex-shrink-0 ml-2" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface BatchVideoGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (params: BatchGenerateVideosRequest) => void;
  loading?: boolean;
  episode?: any;
  videoClips?: IVideoClip[]; // 从父组件传入
}

export default function BatchVideoGenerationModal({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  episode,
  videoClips = []
}: BatchVideoGenerationModalProps) {
  const { models, fetchModels, getModelsByType, getModelById } = useModelStore();
  const { t } = useTranslation(['project', 'common']);
  const [modelsLoading, setModelsLoading] = useState(false);

  const [selectedModels, setSelectedModels] = useState({
    text2videoModelId: '',
    image2videoModelId: '',
    keyframe2videoModelId: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadModels();
    }
  }, [isOpen]);

  useEffect(() => {
    if (episode) {
      setSelectedModels({
        text2videoModelId: episode.text2videoModelId || '',
        image2videoModelId: episode.image2videoModelId || '',
        keyframe2videoModelId: episode.keyframe2videoModelId || ''
      });
    }
  }, [episode]);

  // 计算预估剧点
  const estimatedCoins = useMemo(() => {
    if (!videoClips?.length) return 0;

    let image2videoCount = 0; // 只有startImage的
    let keyframe2videoCount = 0; // 同时有startImage和endImage的

    videoClips.forEach(clip => {
      // 已经有视频的不计算
      if (clip.videoUrl) return;

      // 有图片但没有视频的
      if (clip.startImage) {
        if (clip.endImage) {
          keyframe2videoCount++; // 关键帧模型
        } else {
          image2videoCount++; // 图生视频模型
        }
      }
    });

    // 获取模型价格
    const image2videoModel = selectedModels.image2videoModelId ? getModelById(selectedModels.image2videoModelId) : null;
    const keyframe2videoModel = selectedModels.keyframe2videoModelId ? getModelById(selectedModels.keyframe2videoModelId) : null;

    let total = 0;
    if (image2videoModel?.coinPrice) {
      total += image2videoCount * image2videoModel.coinPrice;
    }
    if (keyframe2videoModel?.coinPrice) {
      total += keyframe2videoCount * keyframe2videoModel.coinPrice;
    }

    // 应用分辨率倍率：1080p 双倍
    const resolutionMultiplier = episode?.resolution === Resolution.FHD_1080P ? 2 : 1;
    return total * resolutionMultiplier;
  }, [videoClips, selectedModels, getModelById, episode?.resolution, models]);

  const loadModels = async () => {
    try {
      setModelsLoading(true);
      await fetchModels();

      const text2video = getModelsByType(ModelType.TEXT_TO_VIDEO);
      const image2video = getModelsByType(ModelType.IMAGE_TO_VIDEO);
      const keyframe2video = getModelsByType(ModelType.KEYFRAME_TO_VIDEO);

      // 设置默认选择
      setSelectedModels(prev => ({
        text2videoModelId: prev.text2videoModelId || episode?.text2videoModelId || text2video[0]?.id || '',
        image2videoModelId: prev.image2videoModelId || episode?.image2videoModelId || image2video[0]?.id || '',
        keyframe2videoModelId: prev.keyframe2videoModelId || episode?.keyframe2videoModelId || keyframe2video[0]?.id || ''
      }));
    } catch (error) {
      console.error('Load models failed:', error);
    } finally {
      setModelsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      text2videoModelId: selectedModels.text2videoModelId || undefined,
      image2videoModelId: selectedModels.image2videoModelId || undefined,
      keyframe2videoModelId: selectedModels.keyframe2videoModelId || undefined
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div className="relative w-full max-w-md overflow-hidden rounded-lg border border-border-subtle bg-bg-elevated p-6 text-left shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <VideoCameraIcon className="w-5 h-5 text-green-400" />
              <h3 className="text-lg font-medium text-gray-50">{t('project:batchVideo.title')}</h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-200 rounded-lg"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {modelsLoading ? (
              <div className="text-center py-4">
                <div className="text-sm text-gray-400">{t('project:batchVideo.loadingModels')}</div>
              </div>
            ) : (
              <>
                {/* 文生视频模型 */}
                <CompactModelSelector
                  label={t('project:batchVideo.text2videoModel')}
                  models={[{ id: '', title: t('project:batchVideo.useDefaultModel') } as IUserModel, ...getModelsByType(ModelType.TEXT_TO_VIDEO)]}
                  value={selectedModels.text2videoModelId}
                  onChange={(id) => setSelectedModels(prev => ({ ...prev, text2videoModelId: id }))}
                  defaultModelLabel={t('project:batchVideo.useDefaultModel')}
                />

                {/* 图生视频模型 */}
                <CompactModelSelector
                  label={t('project:batchVideo.image2videoModel')}
                  models={[{ id: '', title: t('project:batchVideo.useDefaultModel') } as IUserModel, ...getModelsByType(ModelType.IMAGE_TO_VIDEO)]}
                  value={selectedModels.image2videoModelId}
                  onChange={(id) => setSelectedModels(prev => ({ ...prev, image2videoModelId: id }))}
                  defaultModelLabel={t('project:batchVideo.useDefaultModel')}
                />

                {/* 关键帧视频模型 */}
                <CompactModelSelector
                  label={t('project:batchVideo.keyframe2videoModel')}
                  models={[{ id: '', title: t('project:batchVideo.useDefaultModel') } as IUserModel, ...getModelsByType(ModelType.KEYFRAME_TO_VIDEO)]}
                  value={selectedModels.keyframe2videoModelId}
                  onChange={(id) => setSelectedModels(prev => ({ ...prev, keyframe2videoModelId: id }))}
                  defaultModelLabel={t('project:batchVideo.useDefaultModel')}
                />
              </>
            )}

            <div className="bg-bg-form rounded-lg p-3 space-y-2 border border-border-subtle">
              <p className="text-sm text-gray-300">
                {t('project:batchVideo.batchDescription')}
              </p>
              {estimatedCoins > 0 && (
                <div className="flex items-center gap-2 text-sm text-yellow-400">
                  <CurrencyDollarIcon className="w-4 h-4" />
                  <span>{t('project:batchVideo.estimatedCost', { coins: estimatedCoins })}</span>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-bg-form hover:bg-bg-elevated border border-border-subtle rounded-lg transition-colors cursor-pointer"
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || modelsLoading}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors cursor-pointer"
              >
                {loading ? t('common:generating') : t('project:batchVideo.startGenerate')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
